import {
    Button,
    CircularProgress,
    Container,
    CssBaseline,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import sha256 from 'crypto-js/sha256';
import { Form, Formik, FormikProps } from 'formik';
import * as React from 'react';
import { Link, Navigate } from 'react-router-dom';
import * as Yup from 'yup';
import api from '../../api/api';
import { ReactComponent as Logo } from '../../assets/logo.svg';
import useStore from '../../store/store';

interface RegisterInput {
    iso2: string;
    phone: string;
    password: string;
    passwordConfirmation: string;
}

interface RegisterRequest {
    iso2: string;
    phone: string;
    password: string;
}

interface Country {
    Iso2: string;
    Name: string;
    PhoneCode: number;
}

const RegisterSchema = Yup.object().shape({
    iso2: Yup.string().required('Required'),
    phone: Yup.string()
        .required('Required')
        .matches(/^\d{6,15}$/, 'Invalid phone number!'),
    password: Yup.string().required('Required'),
    passwordConfirmation: Yup.string()
        .required('Required')
        .oneOf([Yup.ref('password')], 'Passwords must match'),
});

export default function RegisterFormPage() {
    const accessToken = localStorage.getItem('accessToken');
    const countries = useStore((state) => state.countries);
    const formError = useStore((state) => state.formError);
    const iso2 = localStorage.getItem('iso2') || 'AE';
    const loading = useStore((state) => state.loading);
    const setAccessToken = useStore((state) => state.setAccessToken);
    const setRefreshToken = useStore((state) => state.setRefreshToken);
    const setPhone = useStore((state) => state.setPhone);
    const setFormError = useStore((state) => state.setFormError);
    const setLoading = useStore((state) => state.setLoading);
    const classes = useStyles();
    const [isConfirmDialogOpen, setIsConfirmDialogOpen] = React.useState(false);
    const [leadingPhoneCode, setLeadingPhoneCode] = React.useState('');
    const [pendingFormValues, setPendingFormValues] =
        React.useState<RegisterInput | null>(null);
    const formikRef = React.useRef<FormikProps<RegisterInput>>(null);

    const register = async ({
        iso2,
        phone,
        password,
        passwordConfirmation,
    }: RegisterInput) => {
        if (!leadingPhoneCode) {
            const selectedCountry = countries.find(
                (country: Country) => country.Iso2 === iso2
            );

            if (phone.startsWith(selectedCountry.PhoneCode.toString())) {
                setLeadingPhoneCode(selectedCountry.PhoneCode.toString());
                setPendingFormValues({
                    iso2,
                    phone,
                    password,
                    passwordConfirmation,
                });
                setIsConfirmDialogOpen(true);
                return;
            }

            if (phone.startsWith('0')) {
                setLeadingPhoneCode('0');
                setPendingFormValues({
                    iso2,
                    phone,
                    password,
                    passwordConfirmation,
                });
                setIsConfirmDialogOpen(true);
                return;
            }
        }

        await sendRegistration({ iso2, phone, password });
    };

    const sendRegistration = async ({
        iso2,
        phone,
        password,
    }: RegisterRequest) => {
        setLoading(true);
        try {
            const response = await api.post('/security/register', {
                iso2,
                phone,
                password: sha256(password).toString(),
            });

            const { success, errCode, accessToken, refreshToken } =
                response.data;

            if (success) {
                localStorage.setItem('accessToken', accessToken);
                localStorage.setItem('refreshToken', refreshToken);
                localStorage.setItem('iso2', iso2);
                localStorage.setItem('phone', phone);
                setPhone(phone);
                api.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
                setAccessToken(accessToken);
                setRefreshToken(refreshToken);
            } else {
                setFormError(errCode);
            }
        } catch (e: unknown) {
            if (
                typeof e === 'object' &&
                e !== null &&
                Object.prototype.hasOwnProperty.call(e, 'code')
            ) {
                setFormError((e as { code: string }).code);
            } else {
                console.error('Unexpected error:', e);
                setFormError('UNKNOWN_ERROR');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleConfirmDialogCancel = () => {
        if (pendingFormValues && formikRef.current) {
            const cleanedPhone = pendingFormValues.phone.replace(
                leadingPhoneCode,
                ''
            );
            formikRef.current?.setFieldValue('phone', cleanedPhone);
            setPendingFormValues({
                ...pendingFormValues,
                phone: cleanedPhone,
            });
        }

        setIsConfirmDialogOpen(false);
        setLeadingPhoneCode('');
    };

    const handleConfirmDialogSave = () => {
        if (pendingFormValues) {
            setIsConfirmDialogOpen(false);
            setLeadingPhoneCode('');
            const { iso2, phone, password } = pendingFormValues;
            sendRegistration({ iso2, phone, password });
            setPendingFormValues(null);
        }
    };

    if (accessToken) return <Navigate to="/confirm" />;

    return (
        countries &&
        countries.length > 0 && (
            <>
                <Container component="main" maxWidth="xs">
                    <CssBaseline />
                    <div className={classes.paper}>
                        <Logo className={classes.logo} />
                        <Typography component="h1" variant="h5">
                            Sign up
                        </Typography>
                        <Formik
                            innerRef={formikRef}
                            initialValues={{
                                iso2: iso2,
                                phone: '',
                                password: '',
                                passwordConfirmation: '',
                            }}
                            onSubmit={register}
                            validationSchema={RegisterSchema}
                            validateOnBlur
                            validateOnChange
                        >
                            {({
                                values,
                                errors,
                                touched,
                                handleChange,
                                handleBlur,
                                setFieldValue,
                            }) => (
                                <Form className={classes.form} noValidate>
                                    <FormControl
                                        fullWidth
                                        variant="outlined"
                                        margin="dense"
                                        error={!!(errors.iso2 && touched.iso2)}
                                    >
                                        <InputLabel id="country-label">
                                            Country
                                        </InputLabel>
                                        <Select
                                            labelId="country-label"
                                            id="iso2"
                                            name="iso2"
                                            value={values.iso2}
                                            disabled={loading}
                                            onChange={(e) => {
                                                formError && setFormError(null);
                                                handleChange(e);
                                            }}
                                            onBlur={handleBlur}
                                            label="Country"
                                        >
                                            {countries.map((c: Country) => (
                                                <MenuItem
                                                    key={c.Iso2}
                                                    value={c.Iso2}
                                                >
                                                    {c.Name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {errors.iso2 && touched.iso2 && (
                                            <FormHelperText>
                                                {errors.iso2}
                                            </FormHelperText>
                                        )}
                                    </FormControl>

                                    <TextField
                                        autoComplete="tel-national"
                                        name="phone"
                                        label="Phone"
                                        placeholder="No country code & no leading 0"
                                        fullWidth
                                        value={values.phone}
                                        onChange={(e) => {
                                            formError && setFormError(null);
                                            const numeric =
                                                e.target.value.replace(
                                                    /[^\d]/g,
                                                    ''
                                                );
                                            setFieldValue('phone', numeric);
                                        }}
                                        onBlur={handleBlur}
                                        error={
                                            !!(errors.phone && touched.phone)
                                        }
                                        helperText={
                                            errors.phone && touched.phone
                                                ? errors.phone
                                                : ''
                                        }
                                        variant="outlined"
                                        size="small"
                                        margin="normal"
                                        type="tel"
                                        inputProps={{
                                            inputMode: 'numeric',
                                            pattern: '[0-9]*',
                                        }}
                                        InputLabelProps={{ shrink: true }}
                                    />

                                    <TextField
                                        name="password"
                                        label="Password"
                                        fullWidth
                                        type="password"
                                        variant="outlined"
                                        size="small"
                                        margin="normal"
                                        value={values.password}
                                        onChange={(e) => {
                                            formError && setFormError(null);
                                            handleChange(e);
                                        }}
                                        onBlur={handleBlur}
                                        error={
                                            !!(
                                                errors.password &&
                                                touched.password
                                            )
                                        }
                                        helperText={
                                            errors.password && touched.password
                                                ? errors.password
                                                : ''
                                        }
                                    />

                                    <TextField
                                        name="passwordConfirmation"
                                        label="Repeat Password"
                                        fullWidth
                                        type="password"
                                        variant="outlined"
                                        size="small"
                                        margin="normal"
                                        value={values.passwordConfirmation}
                                        onChange={(e) => {
                                            formError && setFormError(null);
                                            handleChange(e);
                                        }}
                                        onBlur={handleBlur}
                                        error={
                                            !!(
                                                errors.passwordConfirmation &&
                                                touched.passwordConfirmation
                                            )
                                        }
                                        helperText={
                                            errors.passwordConfirmation &&
                                            touched.passwordConfirmation
                                                ? errors.passwordConfirmation
                                                : ''
                                        }
                                    />

                                    {formError && (
                                        <Typography
                                            align="center"
                                            className={classes.errorMessage}
                                            color="error"
                                            variant="body1"
                                        >
                                            Incorrect registration details!
                                        </Typography>
                                    )}

                                    <div className={classes.wrapper}>
                                        <Button
                                            color="primary"
                                            disabled={loading}
                                            fullWidth
                                            type="submit"
                                            variant="contained"
                                        >
                                            Sign Up
                                        </Button>
                                        {loading && (
                                            <CircularProgress
                                                size={24}
                                                className={
                                                    classes.buttonProgress
                                                }
                                            />
                                        )}
                                    </div>
                                    <Link
                                        to="/login"
                                        className={classes.notification}
                                    >
                                        <Typography
                                            align="center"
                                            color="primary"
                                            variant="body1"
                                            className={classes.notification}
                                        >
                                            <b>Already have an account?</b> Sign
                                            in instead!
                                        </Typography>
                                    </Link>
                                </Form>
                            )}
                        </Formik>
                    </div>
                </Container>

                <Dialog
                    open={isConfirmDialogOpen}
                    onClose={handleConfirmDialogCancel}
                    aria-labelledby="form-dialog-title"
                >
                    <DialogTitle id="form-dialog-title">
                        {leadingPhoneCode === '0' ? 'Note' : 'Confirm'}
                    </DialogTitle>
                    <DialogContent>
                        {leadingPhoneCode === '0' ? (
                            <p>
                                The phone number should not start with a 0
                                (zero). It has been removed.
                            </p>
                        ) : (
                            <>
                                <p>
                                    The phone number should not include the
                                    country code.
                                </p>
                                <p>
                                    Is the phone number correct and does not
                                    include the country code (e.g. +
                                    {leadingPhoneCode})?
                                </p>
                            </>
                        )}
                    </DialogContent>
                    <DialogActions>
                        {leadingPhoneCode === '0' ? (
                            <Button
                                onClick={handleConfirmDialogCancel}
                                color="primary"
                            >
                                OK
                            </Button>
                        ) : (
                            <>
                                <Button
                                    onClick={handleConfirmDialogSave}
                                    color="primary"
                                >
                                    Yes
                                </Button>
                                <Button
                                    onClick={handleConfirmDialogCancel}
                                    color="primary"
                                >
                                    No
                                </Button>
                            </>
                        )}
                    </DialogActions>
                </Dialog>
            </>
        )
    );
}

const useStyles = makeStyles((theme) => ({
    buttonProgress: {
        color: 'primary',
        left: '50%',
        marginLeft: -12,
        marginTop: -12,
        position: 'absolute',
        top: '50%',
    },
    errorMessage: {
        marginTop: theme.spacing(2),
    },
    form: {
        marginTop: theme.spacing(1),
        width: '100%',
    },
    logo: {
        marginBottom: theme.spacing(8),
        color: theme.palette.primary.main,
    },
    paper: {
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'column',
        marginTop: theme.spacing(8),
    },
    wrapper: {
        margin: theme.spacing(3, 0, 2),
        position: 'relative',
    },
    notification: {
        color: '#7C87FF',
    },
}));
